import React, { FC, useState } from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import { Theme, Grid, Button, TextField, InputAdornment, Divider } from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp, Search } from '@mui/icons-material';

interface IPaymentsFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setSearchValue: (val: string) => void;
  searchValue: string;
  handleSearch: (clearSearch?: boolean) => void;
}

export const PaymentsFilters: FC<IPaymentsFilters> = ({ isLoading, applyFilters, setSearchValue, handleSearch, searchValue }) => {
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);

  const classes = useStyles({ isMobileFilterButtonOpen });

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="Search..."
            name="search"
            value={searchValue}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  className={classes.searchIcon}
                  onClick={() => {
                    if (searchValue.length > 0) {
                      setHasAppliedFilters(true);
                      handleSearch();
                    }
                  }}
                >
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment
                  position="end"
                  className={classes.searchIcon}
                  onClick={() => {
                    setSearchValue('');
                    handleSearch(true);
                    setHasAppliedFilters(false);
                  }}
                >
                  <Close />
                </InputAdornment>
              ) : null
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && searchValue.length > 0) {
                setHasAppliedFilters(true);
                handleSearch();
              }
            }}
            onChange={e => {
              setSearchValue(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              color="inherit"
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                handleSearch(true);
                setHasAppliedFilters(false);
                setSearchValue('');
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
